import React, { useState } from 'react';
import { Calendar, Clock, MapPin, Users, Share2, CalendarPlus, ArrowRight, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import EventCountdown from './EventCountdown';
import { downloadICS, googleCalendarUrl, shareEvent } from '../utils/calendar';

// Accent theme → tailwind classes.
const ACCENTS = {
  purple: {
    gradient: 'from-violet-600 via-indigo-600 to-purple-700',
    text: 'text-violet-300',
    glow: 'shadow-violet-500/20',
    ring: 'ring-violet-500/30',
    chip: 'bg-violet-500/15 border-violet-400/30 text-violet-200',
  },
  gold: {
    gradient: 'from-amber-500 via-yellow-500 to-orange-500',
    text: 'text-amber-300',
    glow: 'shadow-amber-500/25',
    ring: 'ring-amber-500/30',
    chip: 'bg-amber-500/15 border-amber-400/30 text-amber-100',
  },
  navy: {
    gradient: 'from-slate-800 via-indigo-900 to-slate-900',
    text: 'text-cyan-300',
    glow: 'shadow-cyan-500/20',
    ring: 'ring-cyan-500/30',
    chip: 'bg-cyan-500/10 border-cyan-400/30 text-cyan-200',
  },
};

/**
 * Premium event card — hero image (or gradient fallback), countdown,
 * meta info, primary/secondary CTAs, add-to-calendar + share.
 *
 * `variant` values:
 *   - "full"     : full featured card (Events page)
 *   - "compact"  : condensed card for homepage row
 */
export default function EventCard({ event, variant = 'full' }) {
  const [imgError, setImgError] = useState(false);
  const [showCalMenu, setShowCalMenu] = useState(false);
  const theme = ACCENTS[event.accent] || ACCENTS.navy;
  const isCompact = variant === 'compact';

  const handleShare = async () => {
    await shareEvent(event);
    if (!navigator.share) toast.success('Event link copied to clipboard');
  };

  const heroSlot = (
    <div className={`relative w-full ${isCompact ? 'aspect-[16/10]' : 'aspect-square sm:aspect-[4/5]'} overflow-hidden bg-slate-950`}>
      {event.image && !imgError ? (
        <img
          src={event.image}
          alt={`${event.title} — ${event.subtitle || ''}`}
          onError={() => setImgError(true)}
          className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-[1.03]"
          decoding="async"
          loading="lazy"
        />
      ) : (
        // Gradient fallback with event title typography
        <div className={`w-full h-full bg-gradient-to-br ${theme.gradient} flex flex-col items-center justify-center p-6 text-center relative overflow-hidden`}>
          {/* soft light overlay */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.18),transparent_55%)]" />
          <div className="relative">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.28em] text-white/70 mb-3">{event.formatBadge}</p>
            <h3 className="text-white font-black leading-none text-3xl sm:text-4xl md:text-5xl drop-shadow-lg">
              {event.title}
            </h3>
            {event.subtitle && (
              <p className="text-white/90 font-semibold text-sm sm:text-base mt-3 tracking-wide">{event.subtitle}</p>
            )}
          </div>
        </div>
      )}

      {/* Bottom gradient scrim for readability of badges */}
      <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />

      {/* Format badge */}
      <span className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border backdrop-blur-md ${theme.chip}`}>
        {event.formatBadge}
      </span>

      {/* Countdown on hero */}
      {!isCompact && (
        <div className="absolute bottom-3 right-3">
          <EventCountdown targetISO={event.startISO} compact />
        </div>
      )}
    </div>
  );

  return (
    <article
      className={`group relative flex flex-col rounded-2xl overflow-hidden bg-slate-950/70 border border-white/10 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 hover:border-white/25 shadow-lg hover:shadow-2xl ${theme.glow}`}
      data-testid={`event-card-${event.slug}`}
      data-variant={variant}
    >
      {heroSlot}

      <div className={`flex flex-col flex-1 ${isCompact ? 'p-5' : 'p-6 sm:p-7'}`}>
        <div className="flex-1">
          {/* Tagline (highlights condensed) */}
          {!isCompact && event.tagline && (
            <p className={`text-[11px] uppercase tracking-[0.22em] font-semibold ${theme.text} mb-3`}>{event.tagline}</p>
          )}

          <h3 className={`font-bold text-white leading-tight ${isCompact ? 'text-xl' : 'text-2xl sm:text-3xl'}`}>
            {event.title}
          </h3>
          {event.subtitle && (
            <p className={`${theme.text} font-semibold mt-1 ${isCompact ? 'text-sm' : 'text-base'}`}>{event.subtitle}</p>
          )}

          <p className={`text-slate-300 leading-relaxed mt-4 ${isCompact ? 'text-sm line-clamp-3' : 'text-sm sm:text-base'}`}>
            {event.description}
          </p>

          {/* Meta rows */}
          <div className={`space-y-2 mt-5 ${isCompact ? 'text-xs' : 'text-sm'} text-slate-300`}>
            <div className="flex items-start gap-2.5">
              <Calendar className="h-4 w-4 text-white/60 mt-0.5 flex-shrink-0" />
              <span className="font-medium">{event.dateLabel}</span>
            </div>
            <div className="flex items-start gap-2.5">
              <Clock className="h-4 w-4 text-white/60 mt-0.5 flex-shrink-0" />
              <span>{event.timeLabel}</span>
            </div>
            <div className="flex items-start gap-2.5">
              <MapPin className="h-4 w-4 text-white/60 mt-0.5 flex-shrink-0" />
              <span>
                {event.location}
                {event.locationDetail && (
                  <span className="block text-slate-400 text-xs mt-0.5">{event.locationDetail}</span>
                )}
              </span>
            </div>
            {event.ageRange && (
              <div className="flex items-start gap-2.5">
                <Users className="h-4 w-4 text-white/60 mt-0.5 flex-shrink-0" />
                <span>{event.ageRange}</span>
              </div>
            )}
            {event.capacity && !isCompact && (
              <div className="flex items-start gap-2.5">
                <Users className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <span className="text-amber-200 font-semibold">{event.capacity}</span>
              </div>
            )}
          </div>

          {/* Highlights (full variant only) */}
          {!isCompact && event.highlights?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-1.5">
              {event.highlights.map((h) => (
                <span
                  key={h}
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border ${theme.chip}`}
                >
                  {h}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* CTA row */}
        <div className={`mt-6 flex flex-col sm:flex-row gap-2.5 ${isCompact ? '' : 'sm:gap-3'}`}>
          <Button
            asChild
            className={`flex-1 bg-white text-slate-950 hover:bg-white/90 font-bold ${isCompact ? 'h-10' : 'h-11'}`}
          >
            <a
              href={event.ctaPrimary.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`event-cta-primary-${event.slug}`}
            >
              {event.ctaPrimary.label}
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>

          {!isCompact && event.ctaSecondary && (
            <Button
              asChild
              variant="outline"
              className={`border-white/20 text-white hover:bg-white/10 hover:text-white bg-transparent h-11`}
            >
              <a
                href={event.ctaSecondary.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`event-cta-secondary-${event.slug}`}
              >
                {event.ctaSecondary.label}
                <ExternalLink className="ml-2 h-3.5 w-3.5" />
              </a>
            </Button>
          )}
        </div>

        {/* Utility row: add-to-cal + share (full variant only) */}
        {!isCompact && (
          <div className="mt-4 flex flex-wrap items-center gap-2 pt-4 border-t border-white/5 relative">
            <button
              onClick={() => setShowCalMenu((v) => !v)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white transition-colors"
              data-testid={`event-add-cal-${event.slug}`}
            >
              <CalendarPlus className="h-3.5 w-3.5" /> Add to Calendar
            </button>
            {showCalMenu && (
              <div className="absolute bottom-full left-0 mb-2 z-20 bg-slate-900 border border-white/10 rounded-lg shadow-2xl overflow-hidden min-w-[200px]" data-testid={`event-cal-menu-${event.slug}`}>
                <a
                  href={googleCalendarUrl(event)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-2.5 text-sm text-slate-200 hover:bg-white/5 transition-colors"
                  onClick={() => setShowCalMenu(false)}
                >
                  Google Calendar
                </a>
                <button
                  onClick={() => { downloadICS(event); setShowCalMenu(false); }}
                  className="block w-full text-left px-4 py-2.5 text-sm text-slate-200 hover:bg-white/5 transition-colors border-t border-white/5"
                >
                  Apple / Outlook (.ics)
                </button>
              </div>
            )}
            <span className="text-white/20">·</span>
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-white transition-colors"
              data-testid={`event-share-${event.slug}`}
            >
              <Share2 className="h-3.5 w-3.5" /> Share Event
            </button>
            {event.hostedBy && (
              <span className="ml-auto text-xs text-slate-400">
                Hosted by <span className="text-white font-semibold">{event.hostedBy}</span>
              </span>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
